import { Link } from "@certn/cascadia";
import {
  ActionListItem,
  BasicButton,
  DangerTag,
  Stack,
  SuccessTag,
  Text,
} from "@certn/cascadia-react";
import { EVENT_NAMES, useEventTracking } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface AtsIntegrationRowProps {
  exists?: boolean;
  isEnabled?: boolean;
  icon: string;
  name: string;
  type: string;
  description: string;
  supportLink?: {
    url: string;
    text: string;
  };
  comingSoon?: boolean;
}

export const AtsIntegrationRow = ({
  exists,
  isEnabled,
  icon,
  name,
  type,
  description,
  supportLink,
  comingSoon,
}: AtsIntegrationRowProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useEventTracking();
  const navigate = useNavigate();

  function getTrailingContent() {
    if (comingSoon) return undefined;
    if (exists) {
      return (
        <BasicButton
          onPress={() => navigate(`/settings/integrations/edit/${type}`)}
          testID={`${type}-edit-integration`}
          leftIcon="edit"
        >
          {t("Edit")}
        </BasicButton>
      );
    }
    return (
      <BasicButton
        onPress={() => {
          trackEvent(EVENT_NAMES.CP_ADDED_INTEGRATION, {
            integrationName: name,
          });
          navigate(`/settings/integrations/setup/${type}`);
        }}
        testID={`${type}-setup-integration`}
        leftIcon="add"
      >
        {t("Add")}
      </BasicButton>
    );
  }

  return (
    <ActionListItem
      alignItems="center"
      leading={<img className={styles.icon} src={icon} alt={name} />}
      leadingSize="space-10"
      title={
        <Stack orientation="horizontal" gap="space-2">
          {name}
          {exists &&
            (isEnabled ? (
              <SuccessTag type="subtle">{t("Enabled")}</SuccessTag>
            ) : (
              <DangerTag type="subtle">{t("Disabled")}</DangerTag>
            ))}
        </Stack>
      }
      trailing={getTrailingContent()}
      centerAlignTrailing
    >
      <Text>
        {description}
        {supportLink && (
          <>
            &nbsp;
            <Link
              testID={`${type}-integration-support-link`}
              textColor="text-weak"
              href={supportLink.url}
              isNewTab
            >
              {supportLink.text}
            </Link>
          </>
        )}
      </Text>
    </ActionListItem>
  );
};

const styles = {
  icon: css({
    borderRadius: "var(--border-radius-2)",
  }),
};
